
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { DateTime, Duration } from 'luxon';
import { ActionTypes } from '@/store/actionTypes';
import { MutationTypes } from '@/store/mutationTypes';
import { OutdatedLocationBannerData } from '@/store/types';
import { isNullOrUndefined } from '@/shared/helpers';
import Address from '@/types/Address';
import Customer from '@/types/Customer';
import Driver from '@/types/Driver';
import FbaApiError from '@/types/FbaApiError';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import Truck from '@/types/Truck';
import AlertSidebar from '@/components/AlertSidebar.vue';
import AlreadyHaveTrailerAttached from '@/components/AlreadyHaveTrailerAttached.vue';
import FbxButton from '@/components/fbx-button.vue';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';
import getTrailersAlreadyAttachedToTruck from '@/shared/mixins/getTrailersAlreadyAttachedToTruck';
import useAssociation from '@/shared/composables/useAssociation';
import usePermissionAlert from '@/shared/composables/usePermissionAlert';
import Trailer from '@/types/Trailer';
import Geopoint from '@/types/Geopoint';
import DriverLoaded from '@/components/DriverLoaded.vue';

export default defineComponent({
    name: 'DriverLanding',
    components: {
        AlertSidebar,
        AlreadyHaveTrailerAttached,
        FbxButton,
        SomethingWrongOverlay,
        DriverLoaded,
    },
    mixins: [getTrailersAlreadyAttachedToTruck],
    data(): {
        displayAttachedConfirmation: boolean | null;
        displayLoadedConfirmation: boolean | null;
        currentAttachedTrailers: Array<Trailer>;
        oldLocationAccepted: boolean;
        isHandlingTruckOrLocationUpdate: boolean;
    } {
        return {
            displayAttachedConfirmation: null,
            displayLoadedConfirmation: false,
            currentAttachedTrailers: [],
            oldLocationAccepted: false,
            isHandlingTruckOrLocationUpdate: false,
        };
    },
    setup() {
        const { isLoading: detachIsLoading, updateAssociation: detachTrailer } =
            useAssociation();
        const { setPermissionAlert } = usePermissionAlert();

        return {
            detachIsLoading,
            detachTrailer,
            setPermissionAlert,
        };
    },
    computed: {
        ...mapGetters({
            trailerAssociationsEnabled: 'trailerAssociationsEnabled',
            isDetachError: 'isDetachError',
            driver: 'getDriver',
            truck: 'getTruck',
        }),
        loadedTrailerCompany(): string | null {
            return (
                this.currentAttachedTrailers[0]?.company ?? this.driver.company
            );
        },
        loadedTrailerNumber(): string | null {
            return this.currentAttachedTrailers[0]?.trailerNumber ?? null;
        },
        alertVariables(): OutdatedLocationBannerData {
            return new OutdatedLocationBannerData(
                parseInt(
                    DateTime.now()
                        .diff(this.truck?.telemetryLastReported as DateTime)
                        .toFormat('m')
                ),
                this.truck?.address?.city as string,
                this.truck?.address?.state as string
            );
        },
        availableTrailersAtLocation(): number {
            return this.trailersAtCurrentLocation?.length ?? 0;
        },
        currentNavigationDestination(): string | null | undefined {
            return this.$store.getters.getCurrentNavigationDestination;
        },
        customerAtCurrentLocation(): Customer | null | undefined {
            return this.$store.getters.getCustomerAtCurrentLocation;
        },
        headerText(): string {
            if (this.customerAtCurrentLocation instanceof Customer) {
                return `You are at ${this.customerAtCurrentLocation.name}`;
            } else {
                return `Welcome!`;
            }
        },
        isLoading(): boolean {
            return (
                (this.customerAtCurrentLocation === undefined ||
                    this.truck === undefined ||
                    this.trailersAtCurrentLocation === undefined ||
                    this.currentNavigationDestination === undefined) &&
                this.truck !== null &&
                this.driver !== null &&
                !this.noTruckLocation
            );
        },
        messageText(): string {
            if (this.customerAtCurrentLocation instanceof Customer) {
                if (this.denyTrailersAtCustomerDueToPoolConstraints)
                    return "We don't have any trailers available here due to a customer-required trailer pool.";

                const oneOrMany = `trailer${
                    this.availableTrailersAtLocation === 1 ? '' : 's'
                }`;
                return this.availableTrailersAtLocation > 0
                    ? `We currently have <b>${this.availableTrailersAtLocation} ${oneOrMany}</b> available at this location.`
                    : 'We currently show <b>no trailers</b> available at this location.';
            } else {
                return `Let's get you a trailer.`;
            }
        },
        denyTrailersAtCustomerDueToPoolConstraints(): boolean {
            if (
                this.noTrailerResponsesForCurrentLocation === undefined ||
                (this.noTrailerResponsesForCurrentLocation?.length ?? 0) == 0
            )
                return false;

            const noTrailerResponses =
                this.noTrailerResponsesForCurrentLocation.filter(
                    (result) =>
                        result.customerCode ==
                        this.customerAtCurrentLocation?.code
                );
            if (
                noTrailerResponses === undefined ||
                noTrailerResponses.length == 0
            )
                return false;

            const poolInfo = noTrailerResponses[0].locationPoolInfo;
            if (poolInfo === undefined) return false;

            return (
                !poolInfo.HasAvailablePlusTrailers &&
                poolInfo.HasPool &&
                (!poolInfo.IsVerified || !poolInfo.IsOverPool) &&
                !this.driverHasReservationAtCurrentLocation
            );
        },
        findTrailerButtonText(): string {
            return this.customerAtCurrentLocation instanceof Customer
                ? 'Find a Trailer Elsewhere'
                : 'Find a Trailer Nearby';
        },
        noTruckLocation(): boolean {
            return (
                this.truck instanceof Truck &&
                this.truck.latitude === 0 &&
                this.truck.longitude === 0
            );
        },
        shouldShowSelectATrailer(): boolean {
            return (
                this.customerAtCurrentLocation instanceof Customer &&
                this.availableTrailersAtLocation > 0 &&
                !this.denyTrailersAtCustomerDueToPoolConstraints
            );
        },
        trailersAtCurrentLocation(): NearbyTrailerResult[] | undefined {
            return this.$store.getters.getTrailersAtCurrentLocation;
        },
        noTrailerResponsesForCurrentLocation():
            | NearbyTrailerResult[]
            | undefined {
            return this.$store.getters.getNearbyTrailerResultsDeniedResponses;
        },
        driverHasReservationAtCurrentLocation(): boolean {
            const driverId = this.driver?.id;
            if (
                isNullOrUndefined(driverId) ||
                isNullOrUndefined(this.trailersAtCurrentLocation)
            )
                return false;
            return (
                this.trailersAtCurrentLocation.findIndex(
                    (trailerResult) =>
                        trailerResult.trailers.findIndex(
                            (trailer) =>
                                trailer.reservation?.driverId === driverId
                        ) !== -1
                ) !== -1
            );
        },
        bestAvailableLocation(): Geopoint | undefined {
            return this.$store.getters.getPinnedBestAvailableLocation;
        },
        geoLocationShared(): boolean | undefined {
            return this.$store.getters.getDeviceInfo.isGeoLocationShared;
        },
        geoLocationDeniedWarningDismissed(): boolean | undefined {
            return this.$store.getters.getDeviceInfo
                .isGeoLocationUnsharedWarningDismissed;
        },
        isUsingOldTruckLocation(): boolean {
            const truck = this.truck;
            if (
                isNullOrUndefined(truck) ||
                !(truck instanceof Truck) ||
                isNullOrUndefined(truck.telemetryLastReported) ||
                !(truck.telemetryLastReported instanceof DateTime)
            ) {
                return true;
            }

            const duration = truck.telemetryLastReported.diff(
                DateTime.now(),
                'minutes'
            ) as Duration;
            // 15 minutes ago = -15 not 15
            if (duration.minutes <= -15) {
                return true;
            }
            return false;
        },
        time(): string {
            const truck = this.$store.getters.getTruck;
            if (
                isNullOrUndefined(truck) ||
                !(truck instanceof Truck) ||
                isNullOrUndefined(truck.telemetryLastReported) ||
                !(truck.telemetryLastReported instanceof DateTime)
            ) {
                return 'some time ago';
            }

            return truck.telemetryLastReported.toRelative() || 'some time ago';
        },
        location(): string {
            const truck = this.$store.getters.getTruck;
            if (
                isNullOrUndefined(truck) ||
                !(truck instanceof Truck) ||
                isNullOrUndefined(truck.address) ||
                !(truck.address instanceof Address)
            ) {
                return 'somewhere';
            }
            return truck.address?.city + ', ' + truck.address?.state;
        },
        error(): FbaApiError | undefined {
            return this.$store.getters.getError;
        },
        hasError(): boolean {
            return this?.error?.message !== undefined;
        },
        useDeviceLocation(): boolean {
            return (
                this.$store.getters.driverDeviceLocationFeatureEnabled ?? false
            );
        },
        isGeoLocationShared(): boolean {
            return (
                this.$store.getters.getDeviceInfo.isGeoLocationShared ?? false
            );
        },
        deviceLocationDenied(): boolean {
            return this.useDeviceLocation && !this.isGeoLocationShared;
        },
        isGeoLocationUnsharedWarningDismissed(): boolean {
            return (
                this.$store.getters.getDeviceInfo
                    .isGeoLocationUnsharedWarningDismissed ?? false
            );
        },
    },
    methods: {
        clearAttachedTrailerFields(): void {
            this.currentAttachedTrailers = [];
        },
        closeConfirmation(): void {
            this.displayAttachedConfirmation = false;
            this.displayLoadedConfirmation = false;
        },

        onSearchForTrailers(): void {
            this.closeConfirmation();
        },
        navigateToSelectATrailer(): void {
            this.$router.push({ name: 'SelectTrailer' });
        },
        navigateToTrailerFinder(): void {
            if (this.customerAtCurrentLocation instanceof Customer) {
                this.$store.commit(
                    MutationTypes.SET_EXCLUDE_CUSTOMER,
                    this.customerAtCurrentLocation.id
                );
            }
            this.$router.push({ name: 'FindTrailer' });
        },
        navigateToEnterTrailer(): void {
            this.$router.push({ name: 'EnterTrailer' });
        },
        async onDetachTrailer() {
            this.clearAttachedTrailerFields();
            this.closeConfirmation();
        },
        async onTruckOrLocationUpdated() {
            if (!this.isHandlingTruckOrLocationUpdate) {
                this.isHandlingTruckOrLocationUpdate = true;

                const promiseList = new Array<Promise<void>>();
                promiseList.concat(
                    this.$store.dispatch(
                        ActionTypes.FETCH_CURRENT_NAVIGATION_DESTINATION,
                        undefined
                    )
                );
                promiseList.concat(
                    this.$store.dispatch(
                        ActionTypes.FETCH_CUSTOMER_AND_TRAILERS_AT_CURRENT_LOCATION,
                        undefined
                    )
                );

                if (this.trailerAssociationsEnabled) {
                    promiseList.concat(
                        this.getTrailersAlreadyAttachedToTruck().then(
                            (attachedTrailers) => {
                                if (
                                    !isNullOrUndefined(attachedTrailers) &&
                                    Array.isArray(attachedTrailers)
                                ) {
                                    this.displayAttachedConfirmation = true;
                                    this.currentAttachedTrailers =
                                        attachedTrailers;
                                }
                            }
                        )
                    );
                }

                await Promise.allSettled(promiseList).finally(() => {
                    this.isHandlingTruckOrLocationUpdate = false;
                });

                if (this.deviceLocationDenied) {
                    this.setPermissionAlert();
                }

                if (
                    (!this.useDeviceLocation || this.deviceLocationDenied) &&
                    this.isUsingOldTruckLocation
                ) {
                    this.$store.dispatch(
                        ActionTypes.SET_LOCATION_OUTDATED_WARNING_BANNER,
                        this.alertVariables
                    );
                }
            }
        },
    },
    watch: {
        driver() {
            if (this.driver?.isLoaded === true) {
                this.displayLoadedConfirmation = true;
            }
        },
        truck() {
            this.onTruckOrLocationUpdated();
        },
    },
    async mounted() {
        // Clear any errors that may be lingering in the vuex store from
        // previous sessions/workflows.
        this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
        //This removes the authentication token from the incoming URL
        history.pushState(
            {},
            document.title,
            window.location.origin + window.location.pathname
        );

        if (this.truck !== undefined) {
            const fetchCurrentNavPromise = this.$store.dispatch(
                ActionTypes.FETCH_CURRENT_NAVIGATION_DESTINATION,
                undefined
            );
            const fetchCustomerAndTrailersPromise = this.$store.dispatch(
                ActionTypes.FETCH_CUSTOMER_AND_TRAILERS_AT_CURRENT_LOCATION,
                undefined
            );

            await Promise.allSettled([
                fetchCurrentNavPromise,
                fetchCustomerAndTrailersPromise,
            ]);
        }
    },
});
